// src/components/PromemoriaNoleggi.js
// Promemoria personale per le gru e le piattaforme a noleggio (ottobre 2026).
// Il giorno prima della fine prevista — o appena si entra, se è già passata e il noleggio è
// ancora aperto — chi ha registrato il noleggio e il responsabile della commessa trovano un
// messaggio tra le notifiche: «chiudilo o prorogalo». Un solo messaggio per ogni data di
// fine prevista: se il noleggio viene prorogato, alla nuova data arriva il nuovo promemoria.
//
// Funziona senza il server: ognuno, entrando in Nexiora, crea i promemoria che spettano a
// lui (le regole permettono di leggere le proprie notifiche e di crearne). L'id del
// messaggio è fisso (noleggio + data + situazione + persona), così non si duplica mai.
// Non mostra nulla: lavora in sottofondo e segue i noleggi in tempo reale.
//
// PROMEMORIA SUPERATI (W399): un promemoria vale solo finché la situazione è quella che
// descrive. Se il noleggio viene sospeso, chiuso o prorogato, il messaggio «finisce domani»
// o «da chiudere» non è più vero e resterebbe tra le notifiche a dire una cosa sbagliata:
// viene tolto. Ognuno toglie i propri (le regole permettono di cancellare solo le proprie
// notifiche): subito se è collegato, altrimenti al prossimo accesso.
import { useEffect } from "react";
import { collection, query, where, getDocs, getDoc, setDoc, deleteDoc, onSnapshot, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { scadenzaNoleggio, etichettaNoleggio, testoScadenzaNoleggio } from "../utils/noleggiUtils";

export default function PromemoriaNoleggi() {
  const { currentUser, userProfile, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const uid = currentUser?.uid;
  // Solo nella propria azienda: il Platform Owner che «entra» in un'altra non deve ricevere
  // i promemoria di quell'azienda.
  const propria = !!effectiveOrgId && effectiveOrgId === userProfile?.organizationId;
  const vediImporti = !!(isOrgAdmin || isPlatformOwner);

  useEffect(() => {
    if (!uid || !propria || userProfile?.isDemoVisitor) return undefined;
    let annullato = false;
    let mie = null;                // commesse di cui sono responsabile (lette una volta)
    let inCorso = Promise.resolve();
    const elabora = async (nolDocs) => {
      try {
        if (!mie) {
          const mieSnap = await getDocs(query(collection(db, "projects"), where("organizationId", "==", effectiveOrgId), where("responsabileUid", "==", uid)));
          mie = new Map(mieSnap.docs.map(d => [d.id, d.data()]));
        }
        // i promemoria noleggio che ho già ricevuto
        const giaSnap = await getDocs(query(collection(db, "notifiche"), where("userId", "==", uid), where("orgId", "==", effectiveOrgId), where("tipo", "==", "noleggio_fine")));
        const gia = giaSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const validi = new Set();   // gli id dei promemoria ancora veri adesso
        for (const d of nolDocs) {
          if (annullato) return;
          const n = { id: d.id, ...d.data() };
          if (n.createdBy !== uid && !mie.has(n.projectId)) continue;
          const sc = scadenzaNoleggio(n);
          if (!sc || sc.stato === "senza_data" || sc.stato === "sospeso") continue; // senza data o fermo: lo segnala la Cabina
          // uno per noleggio, data prevista e situazione: quando «domani» diventa «oggi» o «da
          // chiudere», il messaggio vecchio si toglie e resta solo quello vero
          const idMsg = `noleggio_${n.id}_${sc.prevista}_${sc.stato}_${uid}`;
          validi.add(idMsg);
          if (gia.some(x => x.id === idMsg)) continue;
          const ref = doc(db, "notifiche", idMsg);
          if ((await getDoc(ref)).exists()) continue;
          const commessa = mie.get(n.projectId);
          const dove = commessa ? ` su ${commessa.codiceCommessa || commessa.name || "commessa"}` : (n.projectNome ? ` su ${n.projectNome}` : "");
          const testo = vediImporti ? testoScadenzaNoleggio(sc)
            : sc.stato === "domani" ? "Finisce domani: chiudilo o prorogalo."
            : sc.stato === "oggi" ? "Finisce oggi: chiudilo o prorogalo."
            : `Doveva finire ${sc.giorni} giorn${sc.giorni === 1 ? "o" : "i"} fa ed è ancora aperto: chiudilo o prorogalo.`;
          await setDoc(ref, {
            userId: uid, orgId: effectiveOrgId, tipo: "noleggio_fine",
            titolo: sc.stato === "domani" ? "Noleggio in scadenza domani" : "Noleggio da chiudere o prorogare",
            testo: `${etichettaNoleggio(n)}${dove}. ${testo}`,
            noleggioId: n.id, projectId: n.projectId || null,
            letta: false, createdAt: serverTimestamp(),
          });
        }
        // Promemoria superati: noleggio sospeso, chiuso, prorogato o eliminato → si tolgono.
        // Con l'elenco dei noleggi vuoto non si toglie nulla (prudenza: meglio un promemoria
        // in più che cancellarne uno giusto per una lettura non riuscita).
        if (!nolDocs.length) return;
        for (const x of gia) {
          if (annullato) return;
          if (validi.has(x.id)) continue;
          await deleteDoc(doc(db, "notifiche", x.id)).catch(() => {});
        }
      } catch (err) {
        // Un promemoria mancato non deve disturbare il lavoro: resta l'avviso in Cabina di Regia.
        console.warn("Promemoria noleggi non disponibili:", err?.code || err);
      }
    };
    const stop = onSnapshot(query(collection(db, "noleggi"), where("orgId", "==", effectiveOrgId)),
      snap => { inCorso = inCorso.then(() => elabora(snap.docs)); },
      err => console.warn("Promemoria noleggi non disponibili:", err?.code || err));
    return () => { annullato = true; stop(); };
  }, [uid, propria, effectiveOrgId, vediImporti, userProfile?.isDemoVisitor]);

  return null;
}
